import React from 'react';
import { GraphType } from './graphType';
import { QuotationType } from 'features/shared/enums/quotationType';
import MeasuredValueDTO from 'features/shared/dtos/MeasuredValueDTO';

import styles from './measuredDataGraph.module.scss';
import { Bar, Line } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';
import { useTranslation } from 'react-i18next';
import { convertCodeShortToLong } from 'features/shared/helpers/languageHelper';

type Props = {
  graphType: GraphType;
  quotationType: QuotationType;
  realValues: MeasuredValueDTO[];
  realValuesLabel: string;
  realValuesColor: string;
  planValues?: MeasuredValueDTO[];
  planValuesLabel?: string;
  showLegend?: boolean;
  containerClass?: string;
  onLineGraphClick?: (value: MeasuredValueDTO) => void;
  onBarGraphClick?: () => void;
}

const MeasuredDataGraph = ({
  quotationType,
  realValues,
  realValuesLabel,
  realValuesColor,
  planValues,
  planValuesLabel,
  containerClass,
  graphType,
  showLegend = false,
  onLineGraphClick,
  onBarGraphClick
}: Props) => {
  // translations
  const { i18n } = useTranslation();

  const handleLineGraphClick = (activeElements: any[]) => {
    if (!onLineGraphClick || !activeElements || activeElements.length === 0) {
      return;
    }

    const clickedIndex = activeElements[0]?._index;

    if (clickedIndex === undefined || !realValues[clickedIndex]) {
      return;
    }

    onLineGraphClick(realValues[clickedIndex]);
  };

  // helpers
  const getGraphOptions = (): ChartOptions => {
    return {
      maintainAspectRatio: false,
      legend: {
        display: showLegend,
      },
      hover: {
        mode: "index",
        intersect: false,
        animationDuration: 0,
      },
      tooltips: {
        mode: "index",
        intersect: false,
      },
      onClick: (_event: any, activeElements: any[]) => {
        handleLineGraphClick(activeElements);
      },
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
            },
          },
        ],
      },
    };
  }

  const getLineGraphPlugins = () => {
    const drawVerticalLine = (
      ctx: any,
      x: number,
      y: number,
      bottomY: number,
      color: string
    ) => {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, bottomY);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = color;
      ctx.stroke();
      ctx.restore();
    };

    return [
      {
        id: "hover-vertical-line",
        beforeDatasetsDraw: (chart: any) => {
          if (graphType !== "line") {
            return;
          }

          const activePoints = chart.tooltip?._active;
          if (!activePoints || activePoints.length === 0) {
            return;
          }

          const ctx = chart.chart.ctx;
          const bottomY = chart.scales["y-axis-0"].bottom;

          const sortedPoints = [...activePoints].sort((a: any, b: any) => {
            return b._datasetIndex - a._datasetIndex;
          });

          sortedPoints.forEach((point: any) => {
            const { x, y } = point.tooltipPosition();
            const color = chart.data.datasets[point._datasetIndex].borderColor;

            drawVerticalLine(ctx, x, y, bottomY, color);
          });
        },
      },
    ];
  }
  
  const getBarGraphPlugins = () => {
    return [{
      id: 'click-anywhere',
      afterEvent(_chart: any, args: any) {
        if (args.type === 'click' && onBarGraphClick) {
          onBarGraphClick();
        }
      }
    }];
  }

  const getDataObj = () => {
    let data = {
      labels: getLabels(realValues),
      datasets: [
        {
          label: realValuesLabel,
          data: getData(realValues),
          backgroundColor: realValuesColor,
          borderColor: realValuesColor,
          borderWidth: graphType == "line" ? 2 : 0,
          fill: graphType == "line" ? false : true,
          pointRadius: 0,
          pointHoverRadius: graphType == "line" ? 5 : 0,
        }
      ],
    };

    if (planValues && planValuesLabel) {
      data.datasets.push({
        label: planValuesLabel,
        data: getData(planValues),
        backgroundColor: "#cfcfcf", // bar
        borderColor: "#cfcfcf", // line
        borderWidth: graphType == "line" ? 2 : 0,
        fill: graphType == "line" ? false : true,
        pointRadius: 0,
        pointHoverRadius: graphType == "line" ? 5 : 0,
      });
    }

    return data;
  };

  const getLabels = (values: MeasuredValueDTO[]) => {
    const language = convertCodeShortToLong(i18n.language);

    return values.map((item) => {
      if (!item.from) {
        return "";
      }

      const date = new Date(item.from);

      switch (quotationType) {
        case QuotationType.Year:
          return date.toLocaleDateString(language, { year: "numeric" });

        case QuotationType.Month:
          return date.toLocaleDateString(language, { month: "long" });

        case QuotationType.Day:
          return date.toLocaleDateString(language, { day: "2-digit" });

        case QuotationType.Hour:
        case QuotationType.QuarterHour:
          return date.toLocaleTimeString(language, {
            hour: "2-digit",
            minute: "2-digit",
          });

        default:
          return "";
      }
    });
  };

  const getData = (values: MeasuredValueDTO[]) => {
    let result = values.map((item) => (item.value ? item.value : 0));
    return result;
  };

  // sub-renders
  const renderGraph = () => {
    if (graphType == "bar") {
      return (
        <Bar data={getDataObj()} options={getGraphOptions()} plugins={getBarGraphPlugins()} />
      );
    } else if (graphType == "line") {
      return (
        <Line data={getDataObj()} options={getGraphOptions()} plugins={getLineGraphPlugins()} />
      );
    }
  };

  // render
  return (
    <div className={`${styles.container}${containerClass ? " " + containerClass : ""}`}>
      {renderGraph()}
    </div>
  );
}

export default MeasuredDataGraph;